import { useState, useEffect } from "react";
import "./css/adminpayout.css";
import { useNavigate } from "react-router-dom";
import Pagination from "../common/Pagination";
import { apiGet, apiPost } from "../../utils/apiClient";
import { buildPayoutFilterParams, getFileNameFromResponse } from "./payoutFilterParams";

const AdminPayout = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [payouts, setPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [bulkBarBottom, setBulkBarBottom] = useState(40);
  const [modalInfo, setModalInfo] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    const updateBarBottom = () => {
      const footer = document.querySelector(".admin-site-footer");
      if (!footer) return;
      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      setBulkBarBottom(footerTop < viewportHeight ? viewportHeight - footerTop + 16 : 40);
    };
    window.addEventListener("scroll", updateBarBottom);
    updateBarBottom();
    return () => window.removeEventListener("scroll", updateBarBottom);
  }, []);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 오늘 날짜 구하기
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // 필터 상태
  const [filterMode, setFilterMode] = useState("dateRange"); // "dateRange" | "yearMonth"
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: getTodayDate(),
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1),
  });

  const yearOptions = Array.from({ length: 5 }, (_, i) =>
    String(new Date().getFullYear() - i)
  );
  const monthOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));

  // 정산 대기 목록 조회
  useEffect(() => {
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPayouts = async (page = 0, mode = filterMode, activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildPayoutFilterParams(mode, activeFilters);
      params.append("page", page);
      params.append("size", 10);

      const response = await apiGet(`/admin/payout/pending?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setPayouts(data.content || []);
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "정산 목록 조회에 실패했습니다.");
        setPayouts([]);
      }
    } catch (err) {
      setError(err.message || "정산 목록 조회에 실패했습니다.");
      console.error("정산 목록 조회 실패: ", err);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadPayouts(page);
  };

  // 검색
  const handleSearch = () => {
    setCurrentPage(0);
    loadPayouts(0);
  };

  // 필터 초기화
  const handleReset = () => {
    const resetFilters = {
      startDate: "",
      endDate: getTodayDate(),
      year: String(new Date().getFullYear()),
      month: String(new Date().getMonth() + 1),
    };
    setFilterMode("dateRange");
    setFilters(resetFilters);
    setCurrentPage(0);
    loadPayouts(0, "dateRange", resetFilters);
  };

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      const params = buildPayoutFilterParams(filterMode, filters);
      params.append("type", "PENDING");

      const response = await apiGet(`/admin/payout/excel?${params.toString()}`);

      if (response.ok) {
        const fileName = getFileNameFromResponse(response);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert("엑셀 다운로드에 실패했습니다.");
      }
    } catch (err) {
      console.error("엑셀 다운로드 실패:", err);
      alert("엑셀 다운로드에 실패했습니다.");
    }
  };

  // 일괄정산 — 1차 모달 표시
  const handleBulkPayout = () => {
    if (selectedPayouts.length === 0) return;

    const body = {
      storeCodes: selectedPayouts,
      ...(filterMode === "yearMonth"
        ? {
            year: filters.year ? Number(filters.year) : null,
            month: filters.month ? Number(filters.month) : null,
            startDate: null,
            endDate: null,
          }
        : {
            year: null,
            month: null,
            startDate: filters.startDate || null,
            endDate: filters.endDate || null,
          }),
    };

    const settledStores = payouts
      .filter((p) => selectedPayouts.includes(p.storeCode))
      .map((p) => ({
        storeCode: p.storeCode,
        storeNm: p.storeNm,
        payoutFinal: p.payoutFinal ?? 0,
        pendingFinal: p.pendingFinal ?? 0,
      }));

    let filterLabel = null;
    if (filterMode === "yearMonth" && filters.year && filters.month) {
      filterLabel = `${filters.year}년 ${filters.month}월`;
    } else if (filterMode === "dateRange") {
      if (filters.startDate && filters.endDate) filterLabel = `${filters.startDate} ~ ${filters.endDate}`;
      else if (filters.startDate) filterLabel = `${filters.startDate} 이후`;
      else if (filters.endDate) filterLabel = `${filters.endDate} 이전`;
    }

    setModalInfo({ stores: settledStores, filterLabel, body });
  };

  // 일괄정산 — 2차 확인 후 API 호출
  const handleConfirmPayout = async () => {
    try {
      const response = await apiPost("/admin/payout/settle", modalInfo.body);

      if (response.ok) {
        setSelectedPayouts([]);
        loadPayouts(currentPage);
        setModalInfo(null);
        setConfirmVisible(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || "일괄정산 처리에 실패했습니다.");
      }
    } catch (err) {
      console.error("일괄정산 처리 실패:", err);
      alert("일괄정산 처리에 실패했습니다.");
    }
  };

  // 정산 가능 금액 (대기중인 금액 제외) — 0원 이하이면 선택 불가
  const getSettleAmount = (p) => (p.payoutFinal ?? 0) - (p.pendingFinal ?? 0);
  const isSettleable = (p) => getSettleAmount(p) > 0;
  const settleablePayouts = payouts.filter(isSettleable);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPayouts(settleablePayouts.map((p) => p.storeCode));
    } else {
      setSelectedPayouts([]);
    }
  };

  const handleSelectOne = (storeCode) => {
    setSelectedPayouts((prev) =>
      prev.includes(storeCode)
        ? prev.filter((id) => id !== storeCode)
        : [...prev, storeCode]
    );
  };

  return (
    <div className="adminpayout-container">
      <div className="adminpayout-wrap" style={selectedPayouts.length > 0 ? { paddingBottom: 100 } : undefined}>
        {/* 탭 네비게이션 */}
        <div className="adminpayout-tab-navigation">
          <button className="adminpayout-tab-btn adminpayout-tab-active">
            정산 대기
          </button>
          <button
            className="adminpayout-tab-btn"
            onClick={() => {
              navigate("/admin/payout/completed");
            }}
          >
            정산 완료
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="adminpayout-filter-wrap">
          <span className="adminpayout-filter-criteria">결제일 기준</span>
          <div className="adminpayout-filter-row">
            <div className="adminpayout-filter-left">
              <div className="adminpayout-filter-mode-toggle">
                <button
                  className={`adminpayout-mode-btn ${filterMode === "dateRange" ? "adminpayout-mode-active" : ""}`}
                  onClick={() => setFilterMode("dateRange")}
                >
                  날짜 범위
                </button>
                <button
                  className={`adminpayout-mode-btn ${filterMode === "yearMonth" ? "adminpayout-mode-active" : ""}`}
                  onClick={() => setFilterMode("yearMonth")}
                >
                  년월
                </button>
              </div>
              <div className="adminpayout-filter-group">
              {filterMode === "dateRange" ? (
                <>
                  <input
                    type="date"
                    className="adminpayout-date-input"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters({ ...filters, startDate: e.target.value })
                    }
                  />
                  <span className="adminpayout-date-separator">~</span>
                  <input
                    type="date"
                    className="adminpayout-date-input"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters({ ...filters, endDate: e.target.value })
                    }
                  />
                </>
              ) : (
                <>
                  <select
                    className="adminpayout-select"
                    value={filters.year}
                    onChange={(e) =>
                      setFilters({ ...filters, year: e.target.value })
                    }
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}년</option>
                    ))}
                  </select>
                  <select
                    className="adminpayout-select"
                    value={filters.month}
                    onChange={(e) =>
                      setFilters({ ...filters, month: e.target.value })
                    }
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>{m}월</option>
                    ))}
                  </select>
                </>
              )}
              </div>
            </div>
            <div className="adminpayout-search-group">
              <button
                className="adminpayout-search-btn"
                onClick={handleSearch}
              >
                검색
              </button>
              <button
                className="adminpayout-reset-btn"
                onClick={handleReset}
              >
                초기화
              </button>
            </div>
          </div>
        </div>

        {/* 테이블 영역 */}
        <div className="adminpayout-table-wrap">
          <div className="adminpayout-table-header">
            <div className="adminpayout-table-info">
              총 <b>{totalElements}</b>건
            </div>
            <div className="adminpayout-table-actions">
              <button
                className="adminpayout-btn-excel"
                onClick={handleExcelDownload}
              >
                엑셀 다운로드
              </button>
            </div>
          </div>

          {loading ? (
            <div className="adminpayout-loading">로딩 중</div>
          ) : error ? (
            <div className="adminpayout-error">{error}</div>
          ) : (
            <table className="adminpayout-table">
              <thead>
                <tr>
                  <th className="adminpayout-th-checkbox">
                    <input
                      type="checkbox"
                      checked={settleablePayouts.length > 0 && selectedPayouts.length === settleablePayouts.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>가맹코드</th>
                  <th>가맹점명</th>
                  <th>총 매출액</th>
                  <th>PG 수수료</th>
                  <th>자사 수수료</th>
                  <th>실 정산액</th>
                  <th>기타</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", fontWeight: "bold" }}>
                      등록된 정산 내역이 없습니다.
                    </td>
                  </tr>
                ) : (
                  payouts.map((payout) => (
                    <tr
                      key={payout.storeCode}
                      className={selectedPayouts.includes(payout.storeCode) ? "adminpayout-row-selected" : ""}
                    >
                      <td className="adminpayout-td-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPayouts.includes(payout.storeCode)}
                          disabled={!isSettleable(payout)}
                          title={!isSettleable(payout) ? "정산 가능한 금액이 없습니다." : undefined}
                          onChange={() => handleSelectOne(payout.storeCode)}
                        />
                      </td>
                      <td>{payout.storeCode}</td>
                      <td>{payout.storeNm}</td>
                      <td>{payout.payoutPrice?.toLocaleString()}원</td>
                      <td>{payout.payoutBaseTax?.toLocaleString()}원</td>
                      <td>{payout.payoutCustomTax?.toLocaleString()}원</td>
                      <td>{payout.payoutFinal?.toLocaleString()}원</td>
                      <td>
                        <button
                          className="adminpayout-btn-detail"
                          onClick={() => navigate(`/admin/payout/store/${payout.storeCode}`, { state: { storeName: payout.storeNm, filterMode, filters } })}
                        >
                          상세
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* 페이지네이션 */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {modalInfo && (
        <div className="adminpayout-modal-overlay" onClick={() => setModalInfo(null)}>
          <div className="adminpayout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adminpayout-modal-header">
              <span className="adminpayout-modal-title">정산</span>
              <button className="adminpayout-modal-close" onClick={() => setModalInfo(null)}>✕</button>
            </div>
            {modalInfo.filterLabel && (
              <div className="adminpayout-modal-period">
                기간: {modalInfo.filterLabel}
              </div>
            )}
            <div className="adminpayout-modal-table-wrap">
              <table className="adminpayout-modal-table">
                <thead>
                  <tr>
                    <th>가맹코드</th>
                    <th>가맹점명</th>
                    <th>정산액</th>
                    <th>정산대기(제외)</th>
                  </tr>
                </thead>
                <tbody>
                  {modalInfo.stores.map((s) => (
                    <tr key={s.storeCode}>
                      <td>{s.storeCode}</td>
                      <td>{s.storeNm}</td>
                      <td>{(s.payoutFinal - s.pendingFinal).toLocaleString()}원</td>
                      <td>{s.pendingFinal > 0 ? `${s.pendingFinal.toLocaleString()}원` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adminpayout-modal-summary">
              총 {modalInfo.stores.length}건 /&nbsp;
              {modalInfo.stores.reduce((sum, s) => sum + (s.payoutFinal - s.pendingFinal), 0).toLocaleString()}원
              &nbsp;정산 하시겠습니까?
            </div>
            <div className="adminpayout-modal-footer">
              <button className="adminpayout-modal-confirm" onClick={() => setConfirmVisible(true)}>
                확인
              </button>
              <button className="adminpayout-modal-cancel-btn" onClick={() => setModalInfo(null)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmVisible && modalInfo && (
        <div className="adminpayout-modal-overlay" onClick={() => setConfirmVisible(false)}>
          <div className="adminpayout-modal adminpayout-modal-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="adminpayout-modal-header">
              <span className="adminpayout-modal-title">최종 확인</span>
              <button className="adminpayout-modal-close" onClick={() => setConfirmVisible(false)}>✕</button>
            </div>
            <p className="adminpayout-modal-confirm-text">
              선택한 {modalInfo.stores.length}건을 정산 처리합니다.<br />진행하시겠습니까?
            </p>
            <div className="adminpayout-modal-footer">
              <button className="adminpayout-modal-confirm" onClick={handleConfirmPayout}>
                확인
              </button>
              <button className="adminpayout-modal-cancel-btn" onClick={() => setConfirmVisible(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPayouts.length > 0 && (
        <div className="adminpayout-bulk-bar" style={{ bottom: bulkBarBottom }}>
          <div className="adminpayout-bulk-info">
            <span className="adminpayout-bulk-count">
              {selectedPayouts.length}개 선택됨
            </span>
            <span className="adminpayout-bulk-notice">
              PG 미정산 건은 제외하고 정산됩니다.
            </span>
          </div>
          <div className="adminpayout-bulk-bar-actions">
            <button className="adminpayout-btn-bulk" onClick={handleBulkPayout}>
              일괄정산
            </button>
            <button className="adminpayout-btn-cancel" onClick={() => setSelectedPayouts([])}>
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayout;